import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
const tomos = [{
  icon: '📘',
  number: 'Tomo 1',
  title: 'Prehistoria y las Culturas Madre de Mesoamérica',
  subtitle: '40,000 a. C. – 200 a. C. | Los orígenes que tus alumnos nunca olvidarán',
  problem: 'El primer contacto con la historia de México puede ser el más impactante o el más aburrido. Este tomo convierte el origen de una civilización en algo que tus alumnos pueden visualizar, entender y sentir como propio.',
  bullets: ['Cómo llegaron los primeros grupos humanos al territorio mexicano', 'El Pleistoceno, Beringia y la vida nómada explicados con claridad', 'La domesticación del maíz y el nacimiento de la agricultura', 'Los olmecas: cabezas colosales, el jaguar, la escritura y las matemáticas', 'Monte Albán y las culturas del Valle de Oaxaca', 'Por qué estas culturas son la raíz de la identidad mexicana'],
  result: 'Tus alumnos entienden que México no empezó con la Conquista. Entienden que tiene una historia de decenas de miles de años — y eso cambia la forma en que se ven a sí mismos.'
}, {
  icon: '📗',
  number: 'Tomo 2',
  title: 'El Período Clásico, Teotihuacán y los Mayas',
  subtitle: '200 a. C. – 900 d. C. | El momento en que Mesoamérica brilló ante el mundo',
  problem: 'Este es uno de los períodos que más fascina a los alumnos cuando se explica bien. Este tomo te da el material para convertir Teotihuacán y los mayas en algo vivo, no en ruinas polvorientas.',
  bullets: ['El crecimiento de Teotihuacán y su diseño urbano', 'La Calzada de los Muertos, la obsidiana y el comercio mesoamericano', 'Las ciudades-Estado mayas y sus guerras dinásticas', 'La escritura jeroglífica, el uso del cero y la astronomía maya', 'Los calendarios y la arquitectura como sistemas de conocimiento', 'El debate sobre el colapso de las grandes ciudades clásicas'],
  result: 'Tus alumnos descubren que las matemáticas y la astronomía avanzada existían en América mucho antes de que Europa llegara — y eso genera conversaciones que no se olvidan.'
}, {
  icon: '📙',
  number: 'Tomo 3',
  title: 'El Posclásico, los Mexicas y la Conquista',
  subtitle: '900 – 1521 | La historia más mal contada — ahora explicada con honestidad y profundidad',
  problem: 'La Conquista es el tema más sensible y más malinterpretado de la historia de México. Este tomo te da el material para explicarlo con rigor, sin simplificarlo en buenos y malos, y sin que tus alumnos se queden con una versión incompleta.',
  bullets: ['La reorganización de Mesoamérica después del colapso clásico', 'Los toltecas y el ascenso del poder mexica', 'México-Tenochtitlán: fundación, organización y esplendor', 'Las chinampas, el mercado de Tlatelolco y el sistema educativo mexica', 'La llegada de Cortés, Malintzin, Moctezuma y las alianzas indígenas', 'La Noche Triste, la viruela y la caída de Tenochtitlán'],
  result: 'Tus alumnos entienden la Conquista como el encuentro violento y desigual entre dos civilizaciones complejas — no como un cuento de piratas con espadas.'
}, {
  icon: '📕',
  number: 'Tomo 4',
  title: 'El Virreinato de la Nueva España',
  subtitle: '1521 – 1810 | El módulo que más preguntas genera — y mejor resuelto está en este pack',
  problem: 'El Virreinato suele enseñarse como una lista de instituciones y fechas. Este tomo lo convierte en una historia de poder, resistencia, cultura y transformación social que tus alumnos pueden conectar con el México de hoy.',
  bullets: ['La encomienda, la evangelización y las órdenes religiosas', 'La minería de plata, las haciendas y el comercio con Asia', 'La sociedad de castas y la situación de los pueblos indígenas', 'La esclavitud africana y su herencia cultural', 'Sor Juana Inés de la Cruz, el barroco y la cultura novohispana', 'Las reformas borbónicas y las tensiones que prepararon la Independencia'],
  result: 'Tus alumnos entienden que el México actual no empezó en 1810 — y que tres siglos de Virreinato dejaron una huella que todavía se ve, se come y se escucha todos los días.',
  popular: true
}, {
  icon: '📓',
  number: 'Tomo 5',
  title: 'La Independencia y el Siglo XIX',
  subtitle: '1808 – 1876 | Cómo nació México — y cuánto le costó existir',
  problem: 'La Independencia y el siglo XIX están llenos de personajes, guerras y constituciones que se confunden fácilmente. Este tomo los organiza con una narrativa clara que tus alumnos pueden seguir sin perderse.',
  bullets: ['La crisis española, el Grito de Dolores y los primeros insurgentes', 'Hidalgo, Morelos y los Sentimientos de la Nación', 'El Plan de Iguala y la Consumación de la Independencia', 'La guerra con Estados Unidos y la pérdida de más de la mitad del territorio', 'La Constitución de 1857, las Leyes de Reforma y Benito Juárez', 'La Intervención Francesa, Maximiliano y la Restauración de la República'],
  result: 'Tus alumnos entienden que México no nació libre — que tuvo que construir su identidad en medio de invasiones, guerras civiles y traiciones. Eso les cambia la perspectiva de lo que significa ser mexicano.'
}, {
  icon: '📔',
  number: 'Tomo 6',
  title: 'El Porfiriato y la Revolución Mexicana',
  subtitle: '1876 – 1940 | El período que más divide — y que más necesita ser bien explicado',
  problem: 'La Revolución Mexicana es el evento con más mitos y contradicciones de toda la historia de México. Este tomo te da el material para enseñarlo con honestidad y sin simplificaciones que después tus alumnos tengan que desaprender.',
  bullets: ['El Porfiriato: modernización, autoritarismo y desigualdad', 'Las huelgas de Cananea y Río Blanco', 'Madero, Zapata, Villa y Carranza — sus proyectos y sus contradicciones', 'La Decena Trágica y el gobierno de Victoriano Huerta', 'La Constitución de 1917 y sus promesas', 'El cardenismo y la Expropiación Petrolera'],
  result: 'Tus alumnos dejan de ver la Revolución como una película de héroes y villanos. La entienden como un proceso complejo, contradictorio y vigente — porque muchas de sus deudas todavía no están saldadas.'
}, {
  icon: '📒',
  number: 'Tomo 7',
  title: 'El México Moderno y Contemporáneo',
  subtitle: '1940 – 2024 | De dónde viene el México en el que tus alumnos viven hoy',
  problem: 'Este es el tomo que más conecta emocionalmente con los alumnos porque habla del México que conocen. Convierte la historia reciente en una conversación sobre el presente que ningún alumno puede ignorar.',
  bullets: ['El Milagro Mexicano, la industrialización y sus límites', 'El sistema político del PRI y la cultura del partido único', 'El movimiento estudiantil y la matanza de Tlatelolco de 1968', 'Las crisis económicas, el terremoto de 1985 y el nacimiento de la sociedad civil', 'El EZLN, el TLC y la alternancia política de 2000', 'La violencia vinculada al narcotráfico, los derechos humanos y el México actual hasta 2024'],
  result: 'Tus alumnos entienden el México en el que viven. No como algo que les pasó a otros — sino como algo que ellos también están construyendo.'
}];
export function TomosSection() {
  return <section className="py-24 px-4 bg-cream" data-id="element-293">
      <div className="max-w-6xl mx-auto" data-id="element-294">
        <div className="text-center mb-16" data-id="element-295">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-forest" data-id="element-296">
            📘 Los 7 tomos del Mega Pack
          </h2>
          <p className="text-xl text-terracotta font-medium" data-id="element-297">
            Material completo para el aula — desde los primeros pobladores hasta
            2024
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-298">
          {tomos.map((tomo, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} whileHover={{
          y: -5
        }} className={`bg-white rounded-2xl border ${tomo.popular ? 'border-gold shadow-[0_0_30px_rgba(201,162,75,0.2)]' : 'border-beige shadow-sm'} p-8 relative overflow-hidden flex flex-col h-full`} data-id="element-299">
              {tomo.popular && <div className="absolute top-6 right-[-35px] bg-gold text-white font-bold text-xs py-1 px-10 rotate-45 shadow-lg" data-id="element-300">
                  ⭐ EL MÁS POPULAR
                </div>}

              <div className="flex items-center gap-4 mb-6" data-id="element-301">
                <span className="text-4xl" data-id="element-302">{tomo.icon}</span>
                <div data-id="element-303">
                  <div className="text-terracotta font-bold text-sm uppercase tracking-wider" data-id="element-304">
                    {tomo.number}
                  </div>
                  <h3 className="text-2xl font-serif font-bold leading-tight mt-1 text-forest" data-id="element-305">
                    {tomo.title}
                  </h3>
                </div>
              </div>

              <p className="text-ink font-medium mb-6 italic border-l-2 border-gold/50 pl-4" data-id="element-306">
                {tomo.subtitle}
              </p>

              <div className="mb-6" data-id="element-307">
                <h4 className="text-sm font-bold text-warmgray uppercase tracking-wider mb-2" data-id="element-308">
                  ¿Qué resuelve en el aula?
                </h4>
                <p className="text-ink text-sm leading-relaxed" data-id="element-309">
                  {tomo.problem}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-310">
                {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-311">
                    <CheckCircle2 size={18} className="text-olive shrink-0 mt-0.5" data-id="element-312" />
                    <span className="text-ink text-sm font-medium" data-id="element-313">
                      {bullet}
                    </span>
                  </li>)}
              </ul>

              <div className="bg-ivory border border-beige rounded-xl p-4 mt-auto" data-id="element-314">
                <span className="text-forest font-bold block mb-1" data-id="element-315">
                  Resultado:
                </span>
                <p className="text-ink text-sm font-medium" data-id="element-316">{tomo.result}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}